import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, Mail, Phone, Crown, Zap, Star, LogOut, Bell, Loader2, Shield, Globe, Palette,
  Camera, Pencil, Trophy, Award, Flag, Medal, Car, Plane, Glasses, Sparkles, Lock,
} from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader, SectionCard } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const prefLabels = {
  booking_reminders: "Booking Reminders",
  tournament_updates: "Tournament Updates",
  points_rewards: "Points & Rewards",
  promotions: "Promotions",
  system_announcements: "System Announcements",
};

const MEMBERSHIP = {
  basic: { name: "BASIC", color: "#10B981" },
  pro: { name: "PRO", color: "#C0C0C0" },
  champ: { name: "CHAMP", color: "#F59E0B" },
  vip: { name: "VIP", color: "#00e5ff" },
};

// Backend achievements icon (Ionicons) → Lucide component map
const ICON_MAP = {
  flag: Flag, flash: Zap, trophy: Trophy, medal: Medal, podium: Award,
  "car-sport": Car, airplane: Plane, glasses: Glasses,
};

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [prefs, setPrefs] = useState(() => {
    const base = {};
    Object.keys(prefLabels).forEach((k) => { base[k] = user?.notification_preferences?.[k] ?? true; });
    return base;
  });
  const [saving, setSaving] = useState(false);

  // Edit profile dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editBio, setEditBio] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Avatar upload state
  const [uploading, setUploading] = useState(false);

  // Achievements
  const [achLoading, setAchLoading] = useState(true);
  const [achData, setAchData] = useState({ achievements: [], earned: 0, total: 0 });

  useEffect(() => {
    let mounted = true;
    apiClient.get("/achievements/me")
      .then(({ data }) => { if (mounted) setAchData(data); })
      .catch(() => {})
      .finally(() => { if (mounted) setAchLoading(false); });
    return () => { mounted = false; };
  }, []);

  const togglePref = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const savePrefs = async () => {
    setSaving(true);
    try {
      await apiClient.put("/notifications/preferences", { preferences: prefs });
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = () => {
    setEditDisplayName(user?.display_name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim());
    setEditUsername(user?.display_username || "");
    setEditBio(user?.bio || "");
    setEditOpen(true);
  };

  const saveProfile = async () => {
    const uname = editUsername.trim();
    const dname = editDisplayName.trim();
    const bio = editBio.trim();
    if (uname && (uname.length < 3 || uname.length > 32)) { toast.error("Username must be 3-32 characters"); return; }
    if (dname.length > 48) { toast.error("Display name must be under 48 characters"); return; }
    if (bio.length > 280) { toast.error("Bio must be under 280 characters"); return; }
    setSavingProfile(true);
    try {
      const payload = { display_name: dname, bio };
      if (uname) payload.display_username = uname;
      const { data } = await apiClient.patch("/users/me", payload);
      setUser(data);
      setEditOpen(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Update failed"));
    } finally {
      setSavingProfile(false);
    }
  };

  const togglePublic = async (value) => {
    try {
      const { data } = await apiClient.patch("/users/me", { public_profile: value });
      setUser(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Update failed"));
    }
  };

  const onAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset so choosing the same file re-triggers
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/users/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser({ ...user, avatar_url: data.avatar_url });
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();
  const displayName = user?.display_name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const level = Math.floor((user?.total_earned_points || 0) / 5000) + 1;
  const memKey = user?.membership_type;
  const mem = memKey ? MEMBERSHIP[memKey] : null;
  const avatarSrc = user?.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${BACKEND_URL}${user.avatar_url}`)
    : null;

  const earnedAchievements = useMemo(
    () => (achData.achievements || []).filter((a) => a.earned),
    [achData]
  );
  const lockedAchievements = useMemo(
    () => (achData.achievements || []).filter((a) => !a.earned).slice(0, 6),
    [achData]
  );

  return (
    <div>
      <PageHeader title="Profile" subtitle="Manage your account, avatar & trophies." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          {/* Player card */}
          <SectionCard>
            <div className="flex flex-col items-center text-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="relative group"
                data-testid="profile-avatar-picker"
                aria-label="Change avatar"
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={displayName}
                    className="w-28 h-28 rounded-2xl object-cover ring-2 ring-white/10"
                  />
                ) : (
                  <div
                    className="w-28 h-28 rounded-2xl flex items-center justify-center font-display text-3xl font-black text-black"
                    style={{ background: "linear-gradient(135deg, var(--neon-cyan), var(--neon-violet))" }}
                  >
                    {initials || <User className="w-10 h-10" />}
                  </div>
                )}
                <div className="absolute -right-1 -bottom-1 w-8 h-8 rounded-full grid place-items-center bg-[var(--neon-cyan)] shadow-lg ring-2 ring-[#0f1115] group-hover:scale-110 transition-transform">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin text-black" /> : <Camera className="w-4 h-4 text-black" />}
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onAvatarChange}
                className="hidden"
                data-testid="profile-avatar-input"
              />

              <div className="flex items-center gap-2 mt-4">
                <h2 className="font-display text-2xl font-bold text-white">{displayName || "Racer"}</h2>
                <button
                  onClick={openEdit}
                  data-testid="edit-profile-btn"
                  className="w-8 h-8 rounded-full grid place-items-center bg-[var(--neon-cyan)] hover:brightness-110 transition"
                  aria-label="Edit profile"
                >
                  <Pencil className="w-3.5 h-3.5 text-black" />
                </button>
              </div>
              {user?.display_username && (
                <p className="text-[var(--neon-cyan)] font-bold text-sm mt-1">@{user.display_username}</p>
              )}
              {user?.bio && (
                <p className="text-slate-300 text-sm mt-3 whitespace-pre-wrap">{user.bio}</p>
              )}

              <div className="flex items-center gap-2 mt-3 flex-wrap justify-center">
                <div className="chip capitalize" style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}>
                  <Shield className="w-3.5 h-3.5" /> {user?.role}
                </div>
                <div
                  className="chip"
                  style={{
                    background: mem ? `${mem.color}22` : "rgba(148,163,184,0.15)",
                    border: `1px solid ${mem ? mem.color : "rgba(148,163,184,0.4)"}`,
                    color: mem ? mem.color : "#cbd5e1",
                  }}
                >
                  <Crown className="w-3.5 h-3.5" /> {mem?.name || "STANDARD"}
                </div>
              </div>

              <div className="mt-6 space-y-2 text-left w-full">
                <InfoRow icon={Mail} value={user?.email} />
                {user?.phone && <InfoRow icon={Phone} value={user.phone} />}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-slate-800">
              <MiniStat value={`LVL ${level}`} label="Level" />
              <MiniStat value={(user?.points ?? 0).toLocaleString()} label="Points" />
              <MiniStat value={(user?.total_earned_points ?? 0).toLocaleString()} label="Earned" />
            </div>

            <div className="mt-6 flex items-start justify-between gap-3 py-3 border-t border-slate-800">
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Public profile</p>
                <p className="text-slate-500 text-xs mt-0.5">Show avatar, tier & badges to others</p>
              </div>
              <Switch
                checked={!!user?.public_profile}
                onCheckedChange={togglePublic}
                data-testid="public-profile-toggle"
              />
            </div>

            <button onClick={logout} className="neon-outline-btn w-full py-3 mt-4" data-testid="profile-logout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </SectionCard>

          <ThemeSelector />
        </div>

        <div className="lg:col-span-2 space-y-6">
          {/* Trophies */}
          <SectionCard>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
                <Trophy className="w-5 h-5" style={{ color: "#F59E0B" }} /> Trophies
              </h3>
              <div className="text-xs font-bold text-slate-400 tracking-widest">
                {achLoading ? "..." : `${achData.earned} / ${achData.total} EARNED`}
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-5">Badges you&apos;ve earned across bookings, sessions, and tournaments.</p>

            {achLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading achievements…
              </div>
            ) : earnedAchievements.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center border border-dashed border-slate-800 rounded-xl">
                <Sparkles className="w-6 h-6 mx-auto mb-2 text-slate-600" />
                No trophies yet. Book a session and start earning.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {earnedAchievements.map((a) => (
                  <TrophyCard key={a.id} achievement={a} earned />
                ))}
              </div>
            )}

            {lockedAchievements.length > 0 && (
              <>
                <div className="mt-6 mb-2 text-xs font-bold tracking-widest text-slate-500">UP NEXT</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {lockedAchievements.map((a) => (
                    <TrophyCard key={a.id} achievement={a} />
                  ))}
                </div>
              </>
            )}
          </SectionCard>

          <SectionCard>
            <h3 className="font-display text-lg font-bold text-white mb-1 flex items-center gap-2">
              <Globe className="w-5 h-5" style={{ color: "#00ff9d" }} /> Booking Time Zone
            </h3>
            <p className="text-slate-400 text-sm">
              All bookings and times across Pit Lane are shown in <b className="text-white">Atlantic Time (Halifax)</b> — the lounge&apos;s local time zone. There&apos;s nothing to configure.
            </p>
          </SectionCard>

          <SectionCard>
            <h3 className="font-display text-lg font-bold text-white mb-1 flex items-center gap-2">
              <Bell className="w-5 h-5" style={{ color: "#00e5ff" }} /> Notification Preferences
            </h3>
            <p className="text-slate-400 text-sm mb-5">Choose what alerts you receive.</p>
            <div className="space-y-3">
              {Object.entries(prefLabels).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <span className="text-slate-200 font-semibold">{label}</span>
                  <button onClick={() => togglePref(key)} data-testid={`pref-${key}`}
                    className="w-12 h-7 rounded-full relative transition-all"
                    style={{ background: prefs[key] ? "var(--neon-cyan)" : "rgba(120,150,220,0.2)" }}>
                    <span className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all"
                      style={{ left: prefs[key] ? "26px" : "4px" }} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={savePrefs} disabled={saving} className="neon-btn px-6 py-3 mt-6" data-testid="save-prefs">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save Preferences"}
            </button>
          </SectionCard>

          <SectionCard className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6" style={{ color: "#c4b5fd" }} />
              <div>
                <p className="font-display font-bold text-white">Membership</p>
                <p className="text-slate-400 text-sm">{user?.is_member ? "Active — manage your plan" : "Upgrade for premium perks"}</p>
              </div>
            </div>
            <button onClick={() => navigate("/membership")} className="neon-outline-btn px-5 py-2.5" data-testid="profile-membership">
              {user?.is_member ? "Manage" : "Upgrade"}
            </button>
          </SectionCard>
        </div>
      </div>

      {/* Edit profile dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0f1115] border-slate-800 max-w-md" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest">EDIT PROFILE</DialogTitle>
            <DialogDescription>Update how other racers see you.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Display name</label>
              <input
                type="text"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                maxLength={48}
                placeholder="Your public display name"
                className="input-cyber"
                data-testid="edit-display-name-input"
              />
            </div>
            <div>
              <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Username</label>
              <input
                type="text"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
                maxLength={32}
                autoCapitalize="none"
                placeholder="Unique @handle"
                className="input-cyber"
                data-testid="edit-username-input"
              />
            </div>
            <div>
              <label className="text-white text-xs font-bold tracking-widest mb-1.5 block">Bio</label>
              <textarea
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                maxLength={280}
                rows={4}
                placeholder="Say a little about yourself (max 280 chars)"
                className="input-cyber resize-none"
                data-testid="edit-bio-input"
              />
              <p className="text-slate-500 text-xs mt-1 text-right">{editBio.length}/280</p>
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setEditOpen(false)}
              className="neon-outline-btn px-5 py-2.5"
              data-testid="edit-profile-cancel"
            >
              Cancel
            </button>
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="neon-btn px-6 py-2.5"
              data-testid="edit-profile-save"
            >
              {savingProfile ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const InfoRow = ({ icon: Icon, value }) => (
  <div className="flex items-center gap-3 text-slate-300 text-sm">
    <Icon className="w-4 h-4 text-slate-500 shrink-0" />
    <span className="truncate">{value}</span>
  </div>
);

const MiniStat = ({ value, label }) => (
  <div className="text-center">
    <p className="font-display font-black text-white text-base">{value}</p>
    <p className="text-slate-500 text-[10px] uppercase tracking-widest mt-0.5">{label}</p>
  </div>
);

const TrophyCard = ({ achievement, earned = false }) => {
  const Icon = ICON_MAP[achievement.icon] || Trophy;
  const pct = Math.round((achievement.progress || 0) * 100);
  return (
    <div
      className="rounded-xl p-3 border transition-all"
      style={{
        background: earned ? "color-mix(in srgb, var(--neon-cyan) 8%, transparent)" : "rgba(15,17,21,0.6)",
        borderColor: earned ? "color-mix(in srgb, var(--neon-cyan) 45%, transparent)" : "rgba(120,150,220,0.18)",
        boxShadow: earned ? "0 0 20px -8px color-mix(in srgb, var(--neon-cyan) 55%, transparent)" : "none",
      }}
      data-testid={`trophy-${achievement.id}`}
    >
      <div className="flex items-start gap-2">
        <div
          className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
          style={{ background: earned ? "var(--neon-cyan)" : "rgba(148,163,184,0.15)" }}
        >
          {earned ? (
            <Icon className="w-4 h-4 text-black" />
          ) : (
            <Lock className="w-3.5 h-3.5 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{achievement.title}</p>
          <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">{achievement.desc}</p>
        </div>
      </div>
      {!earned && (
        <div className="mt-2">
          <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-[var(--neon-cyan)] rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-slate-500 text-[10px] mt-1 tracking-wider">
            {achievement.current}{achievement.unit} / {achievement.target}{achievement.unit}
          </p>
        </div>
      )}
    </div>
  );
};

const THEME_OPTIONS = [
  { key: "gaming", label: "Gaming", color: "#00e5ff" },
  { key: "racing", label: "Racing", color: "#ff3860" },
  { key: "flight", label: "Flight", color: "#8b5cf6" },
  { key: "golf", label: "Golf", color: "#00e07a" },
];

const ThemeSelector = () => {
  const { theme, setTheme } = useTheme();
  return (
    <SectionCard data-testid="theme-selector">
      <div className="flex items-center gap-3 mb-4">
        <Palette className="w-5 h-5" style={{ color: "#ffb020" }} />
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider">Theme</h3>
      </div>
      <p className="text-slate-400 text-sm mb-4">Pick the vibe. Fonts, colors, and background all swap with the theme.</p>
      <div className="grid grid-cols-2 gap-2">
        {THEME_OPTIONS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTheme(t.key)}
            data-testid={`theme-${t.key}`}
            className="rounded-xl p-3 text-left transition-colors"
            style={{
              background: theme === t.key ? `${t.color}22` : "rgba(8,11,22,0.6)",
              border: `1px solid ${theme === t.key ? t.color : "rgba(120,150,220,0.14)"}`,
            }}
          >
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: t.color }} />
              <span className="text-white font-display font-bold uppercase text-xs tracking-widest">{t.label}</span>
            </div>
          </button>
        ))}
      </div>
    </SectionCard>
  );
};
